import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, Link, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import axios from "../utils/axios";
import { useAuth } from "../contexts/AuthContext";
import { parseReturnUrl } from "../utils/authUtils";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      const response = await axios.post("/api/auth/login", data);

      // Store user data and token
      login(response.data.user, response.data.token);

      toast.success(`Welcome back, ${response.data.user.name}!`);

      // Get return URL from state or query params
      const returnUrl =
        location.state?.from?.pathname ||
        parseReturnUrl(new URLSearchParams(location.search));

      // Redirect based on return URL or user role
      const redirectPath = getRedirectPath(response.data.user.role, returnUrl);
      navigate(redirectPath, { replace: true });
    } catch (error) {
      console.error("Login error:", error);
      const errorMessage =
        error.response?.data?.message || "Login failed. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRedirectPath = (role, returnUrl) => {
    // If there's a return URL and it's not the login page, redirect there
    if (returnUrl && returnUrl !== "/" && returnUrl !== "/login") {
      return returnUrl;
    }

    // Otherwise redirect based on role
    switch (role) {
      case "admin":
        return "/admin";
      case "sales":
        return "/sales-dashboard";
      case "leads":
        return "/leads-dashboard";
      case "orders":
        return "/orders-dashboard";
      case "telecaller":
        return "/telecaller-dashboard";
      case "customer":
        return "/";
      default:
        return "/";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          {/* <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome Back to TamilSandhai
          </h2>
          <p className="text-gray-600">
            Sign in to access your dashboard, manage appointments, and explore
            our exclusive products & services
          </p> */}
          {/* <div className="mt-4 text-sm text-blue-600">
            🎯 Coming from our Facebook, Instagram, or YouTube ad?
            <a
              href="/register"
              className="underline font-medium hover:text-blue-800"
            >
              Register here for your free consultation
            </a>
          </div> */}
        </div>

        <div className="bg-white shadow-xl rounded-lg px-6 py-8">
          <h2 className="text-2xl text-center font-bold text-gray-900 mb-2">
            LOGIN
          </h2>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Please enter a valid email address",
                  },
                })}
                className="input"
                placeholder="Enter your email"
                autoComplete="email"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Password *
              </label>
              <input
                type="password"
                id="password"
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters",
                  },
                })}
                className="input"
                placeholder="Enter your password"
                autoComplete="current-password"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary btn-lg relative"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="loading-dots mr-2">
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                  </div>
                  Signing In...
                </div>
              ) : (
                "Sign In"
              )}
            </button>

            {/* Additional Links */}
            <div className="text-center space-y-2">
              <Link
                to="/forgot-password"
                className="text-sm text-indigo-600 hover:text-indigo-500"
              >
                Forgot your password?
              </Link>
            </div>
          </form>

          {/* Demo Users Section */}
          {/* <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              Demo Login Credentials:
            </h3>
            <div className="space-y-2 text-xs text-gray-600">
              <div className="flex justify-between">
                <span className="font-medium">Admin:</span>
                <span>admin / password123</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Sales:</span>
                <span>sales / password123</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Leads:</span>
                <span>leads / password123</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Orders:</span>
                <span>orders / password123</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Telecaller:</span>
                <span>telecaller / password123</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Customer:</span>
                <span>Register as customer below</span>
              </div>
            </div>
          </div> */}

          {/* Registration Links */}
          <div className="mt-6 text-center space-y-3">
            <div className="text-sm ">Don't have an account?</div>
            <div className="space-y-2">
              <Link
                to="/register?type=lead"
                className="block w-full btn-secondary btn-sm"
              >
                🎯 Free Sales Call Registration (3-10 mins)
              </Link>
              <Link
                to="/register?type=customer"
                className="block w-full btn-secondary btn-sm"
              >
                Customer Registration
              </Link>
              <Link
                to="/register?type=staff"
                className="block w-full btn-secondary btn-sm"
              >
                Staff Registration
              </Link>
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500 mb-2">
            🔒 Your information is secure and encrypted
          </p>
          <p className="text-xs text-gray-400">
            ✨ Join our platform to access exclusive features
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
